import { Skeleton } from '@/components/ui/skeleton'
import { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

interface StatsCardProps {
  title: string
  value: number | string
  description?: string
  icon?: LucideIcon
  color?: string
  highlight?: boolean
  isLoading?: boolean
  className?: string
  onClick?: () => void
}

export function StatsCard({
  title,
  value,
  description,
  icon: Icon,
  color = 'bg-gray-50 text-gray-600',
  highlight = false,
  isLoading,
  className,
  onClick,
}: StatsCardProps) {
  if (isLoading) {
    return (
      <div className={cn('card rounded-lg p-5', className)}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <Skeleton className="h-4 w-24 mb-3" />
            <Skeleton className="h-8 w-16 mb-2" />
            <Skeleton className="h-3 w-32" />
          </div>
          <Skeleton className="h-10 w-10 rounded-lg" />
        </div>
      </div>
    )
  }

  const Wrapper = onClick ? 'button' : 'div'

  return (
    <Wrapper
      onClick={onClick}
      className={cn(
        'card w-full rounded-lg p-5 text-left transition-all duration-200',
        highlight && 'border-amber-300 ring-1 ring-amber-300',
        onClick && 'cursor-pointer hover:shadow-md active:scale-[0.98]',
        className
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
          {description && (
            <p className="text-xs text-gray-500 mt-1">{description}</p>
          )}
        </div>
        {Icon && (
          <div className={cn('p-2.5 rounded-lg', color)}>
            <Icon className="h-5 w-5" />
          </div>
        )}
      </div>
    </Wrapper>
  )
}
